import React from 'react';
import styled from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { Modal, PrimaryButton } from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import { theme } from 'lib/style';
import { removeUserFromDepartment } from 'lib/api';
import { useToastError } from 'lib/hooks';
import { successToast } from 'lib/components/toasts/success';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};

  font-style: normal;
  font-size: 18px;
  line-height: 24px;
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 600px;
  margin-top: 24px;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
`;

type Props = {
  handleModalClose: (shouldRefresh?: boolean) => void;
  selectedUser?: any;
  departmentId?: any;
  customerId?: any;
};

export const RemoveUserFromDepartmentModal = (props: Props) => {
  const [loading, setLoading] = React.useState(false);

  const { handleModalClose, selectedUser, departmentId, customerId } = props;

  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  const { showError } = useToastError();

  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const removeUserFromDept = async () => {
    setLoading(true);
    try {
      await removeUserFromDepartment(
        { userIds: selectedUser, departmentIds: departmentId },
        customerId
      );
      setLoading(false);
      successToast({ title: 'User(s) removed successfully!' });
      handleModalClose(true);
    } catch (error) {
      showError(error);
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <Modal style={{ position: 'fixed' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>Are you sure?</HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose()}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        <Content>Selected users will be removed from the Department.</Content>
        <Footer>
          <PrimaryButton
            text='No, Cancel'
            textColor={theme.palette.black_1_100}
            backgroundColor={theme.palette.primaryDarkBlue10Percent}
            borderRadius={5}
            fontSize='15px'
            textPadding='8px 16px'
            margin='0px 10px 0px 0px'
            onClick={async () => {
              handleModalClose();
            }}
            disabled={loading}
          />
          <PrimaryButton
            text='Yes, Remove'
            textColor={theme.palette.white}
            backgroundColor={theme.palette.buttonDanger}
            borderRadius={5}
            fontSize='15px'
            textPadding='8px 16px'
            onClick={async () => {
              await removeUserFromDept();
            }}
            disabled={loading}
          />
        </Footer>
      </div>
    </Modal>
  );
};
